import * as immutable from 'immutable';
import { IgenomicsAction } from '../../core/interfaces/genomics-action.interface';
import { FILTER_ACTIONS } from './filter.actions';
import { Filter, FilterAndCategories } from '../classes/filter';
import { cloneDeep, isEmpty } from 'lodash';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { WorkflowDrivenCacheablesEntity } from '@ispm/workflow/entities/workflow-driven-cacheables.entity';
import { CORE_ACTIONS } from '../../core/constants/core-actions.constant';
import { FilterUtils } from '../utils/filter-utils';

/** Possible values for "current":
 *    for aberrations filters: "aberration_snv", "aberration_cnv", "aberration_fusion".
 *    for curation filters: "curation_worklist", "currationInfo_pathways", "curration_committeeReviews"
 */
export interface IFiltersState {
    // The map key is the current filterType and filterSubtype and the value (map) is
    // a map that its key is the filter id and the value is the filter object (filter+ categories).
    applied: immutable.Map<string, immutable.Map<string, Filter>>;
    // The key is the filterTypeAndSubtype and the value is the corresponding categories.
    defaultCategories: immutable.Map<string, any>;
    // Applied filter properties from ll categories for a filter
    appliedFilterProperties: immutable.Map<string, any[]>;
    // The currentSelected map key is the filter id and the value is the current selected filter.
    // When the "Clear filters values" is clicked, the category of the selected filter is changed in this
    // map while the applied map remains unchanged.
    currentSelected: immutable.Map<string, FilterAndCategories>;
    currentSelectedQuery: immutable.Map<string, any>;
    currentSelectedFormData: immutable.Map<string, any>;
    currentSelectedConfig: immutable.Map<string, any>;
    current: string;
    currentSelectedTab: string;
    formFilter:immutable.Map<string, IFilterForm>;
}

export interface IFilterForm {
    isForm: boolean;
    categoryBinding?: {
      [categoryId: string]: {
        formId: string;
        formVertical: boolean;
      };
    };

}

const InitState: IFiltersState = {
    applied: immutable.Map<string, immutable.Map<string, Filter>>(),
    defaultCategories: immutable.Map<string, any>(),
    appliedFilterProperties: immutable.Map<string, any[]>(),
    currentSelected: immutable.Map<string, FilterAndCategories>(),
    currentSelectedQuery: immutable.Map<string, any>(),
    currentSelectedFormData: immutable.Map<string, any>(),
    currentSelectedConfig: immutable.Map<string, any>(),
    current: '',
    currentSelectedTab: null,
    formFilter:immutable.Map<string, IFilterForm>(),
};

export const selectFiltersStore = createFeatureSelector<IFiltersState>('filters');
export const getAppliedFilters = createSelector(selectFiltersStore, (filtersState) => filtersState.applied);
export const getDefaultCategories = createSelector(selectFiltersStore, (filterState) => filterState.defaultCategories);
export const getAppliedFilterProperties = createSelector(selectFiltersStore, (filtersState) => filtersState.appliedFilterProperties);
export const getCurrentSelected = createSelector(selectFiltersStore, (filtersState) => filtersState.currentSelected);
export const getCurrentSelectedQuery = createSelector(selectFiltersStore, (filtersState) => filtersState.currentSelectedQuery);
export const getCurrentSelectedConfig = createSelector(selectFiltersStore, (filtersState) => filtersState.currentSelectedConfig);
export const getCurrentFilters = createSelector(selectFiltersStore, (filtersState) => filtersState.current);

const storeToSessionCache = function (state: IFiltersState) { // , userId: string) {
                                                              // let key = `uc-${userId}`;
                                                              // let storedState = sessionStorage.getItem(key);
                                                              // storedState = storedState ? JSON.parse(storedState) : {};
                                                              // Object.assign(storedState, {'filters': state});
                                                              // localStorage.setItem(key, JSON.stringify(storedState));
    const stateStr: string = JSON.stringify(state);
    sessionStorage.setItem('filters', stateStr);
};

export function filterReducer(state: IFiltersState = InitState, action: IgenomicsAction<WorkflowDrivenCacheablesEntity | any>): IFiltersState {
    let results;
    let current: string;
    let currentAppliedFilters;
    let currentSelectedFilter;

    switch (action.type) {
        case FILTER_ACTIONS.STORE_STATE:
            state = Object.assign({ }, state);
            storeToSessionCache(state); // , action.payload.userID);
            return state;
        case FILTER_ACTIONS.RESTORE_STATE:
            const filtersFromSessionCache = FilterUtils.loadFromSessionCache();
            state.current = filtersFromSessionCache.current;
            const appliedFilters = filtersFromSessionCache.applied.get(filtersFromSessionCache.current);
            results = immutable.Map<string, Filter>(appliedFilters);
            results = results.asMutable();
            state.applied = state.applied.set(filtersFromSessionCache.current, results.asImmutable());

            Array.from(filtersFromSessionCache.appliedFilterProperties.entries()).forEach(entry => {
                state.appliedFilterProperties = state.appliedFilterProperties.set(entry[0], entry[1]);
            });

            Array.from(filtersFromSessionCache.defaultCategories.entries()).forEach(entry => {
                state.defaultCategories = state.defaultCategories.set(entry[0], entry[1]);
            });

            Array.from(filtersFromSessionCache.currentSelected.entries()).forEach(entry => {
                state.currentSelected = state.currentSelected.set(entry[0], entry[1]);
            });

            Array.from(filtersFromSessionCache.currentSelectedQuery.entries()).forEach(entry => {
                state.currentSelectedQuery = state.currentSelectedQuery.set(entry[0], entry[1]);
            });

            Array.from(filtersFromSessionCache.currentSelectedFormData.entries()).forEach(entry => {
                state.currentSelectedFormData = state.currentSelectedFormData.set(entry[0], entry[1]);
            });

            Array.from(filtersFromSessionCache.currentSelectedConfig.entries()).forEach(entry => {
                state.currentSelectedConfig = state.currentSelectedConfig.set(entry[0], entry[1]);
            });

            return { ...state};
        case FILTER_ACTIONS.SET_CURRENT:
            state.current = action.payload.filterTypeAndSubtype;
            return { ...state};
        case FILTER_ACTIONS.SET_CURRENT_SELECTED_TAB:
            state.currentSelectedTab = action.payload.currentSelectedTab;
            return { ...state};
        case FILTER_ACTIONS.SET_DEFAULT_CATEGORIES:
            state.current = action.payload.filterTypeAndSubtype;
            if (!state.defaultCategories.has(action.payload.filterTypeAndSubtype)) {
                const defaultCategories = cloneDeep(action.payload.categories);
                state.defaultCategories = state.defaultCategories.set(action.payload.filterTypeAndSubtype, defaultCategories);
            }
            return { ...state};
        case CORE_ACTIONS.CLEAR_ALL_CACHED_ITEMS:
        case FILTER_ACTIONS.SET_APPLIED_FILTER_PROPERTIES:
            if (action.payload.filterTypeAndSubtype === '') {
                current = state.current;
            } else {
                current = action.payload.filterTypeAndSubtype;
            }
          state.appliedFilterProperties = state.appliedFilterProperties.set(current, cloneDeep(action.payload.appliedFilterProperties));
            return { ...state};
        case FILTER_ACTIONS.SET_CURRENT_FILTERS:
            state.current = action.payload.defaultFilterType;
            results = immutable.Map<string, Filter>();
            results = results.asMutable();
            if (action.payload.filters) {
                Array.from(action.payload.filters.entries()).forEach(entry => {
                    results.set(entry[0], entry[1]);
                });
            }
            state.applied = state.applied.set(action.payload.filterType, results.asImmutable());
            const categories = cloneDeep(action.payload.categories);
            const filterAndCategories: any = {
                categories: categories
            };
            let defaultFilter = cloneDeep(action.payload.defaultFilter);
            if (action.payload.defaultFilter) {
                filterAndCategories.filter = defaultFilter;
            } else {
                defaultFilter = {
                    name: '',
                    query: ''
                };
                filterAndCategories.filter = defaultFilter;
            }

            state.currentSelected = state.currentSelected.set(action.payload.filterType, filterAndCategories);
            state.currentSelectedQuery = state.currentSelectedQuery.set(action.payload.filterType, action.payload.defaultFilterQueryObj);
            state.currentSelectedFormData = setCurrentSelectedFormData(state.currentSelectedFormData, action.payload.filterType, action.payload.filterFormData, false);
            state.currentSelectedConfig = state.currentSelectedConfig.set(action.payload.filterType, action.payload.filterConfig);

            // NOTE: in aberrations filter the defaultFilterType is ALWAYS '"aberration_snv"' and therefore other filter-types
            //       (cnv / fusions) are not entered to the map. As a result, when deleting a Filter, the filter panel
            //       displays no categories for cnv / fusions. the correct value to set is 'action.payload.filterType'
            if (!state.defaultCategories.has(action.payload.filterType)) {
                const defaultCategories: any = cloneDeep(action.payload.categories);
                state.defaultCategories = state.defaultCategories.set(action.payload.filterType, defaultCategories);
            }
            if(action.payload.formFilter){
                let formFilter = state.formFilter;
                formFilter = formFilter.set(action.payload.filterType,action.payload.formFilter);
                state.formFilter = formFilter;
            } else {
                let formFilter = state.formFilter;
                formFilter = formFilter.set(action.payload.filterType,{isForm:false});
                state.formFilter = formFilter;
            }
            return { ...state};
        case FILTER_ACTIONS.SET_SELECTED_FILTER:
            if (state.currentSelected.has(action.payload.filterTypeAndSubtype)) {
                currentSelectedFilter = state.currentSelected.get(action.payload.filterTypeAndSubtype);
                if(currentSelectedFilter.hasOwnProperty('filter') && !isEmpty(currentSelectedFilter['filter'])) {
                    if (currentSelectedFilter && currentSelectedFilter.filter.id !== action.payload.filter.id) {
                        const selectedFilter = {
                            filter: action.payload.filter,
                            categories: cloneDeep(action.payload.categories) // With out clone store is mutated without any action
                        };
                        state.currentSelected = state.currentSelected.set(action.payload.filterTypeAndSubtype, selectedFilter);
                    }
                }
                else if (currentSelectedFilter && currentSelectedFilter.id == action.payload.filterId) {
                    currentSelectedFilter.categories = action.payload.categories;
                    // state.currentSelected = state.currentSelected.set(action.payload.filterTypeAndSubtype, currentSelectedFilter);
                }
                else {
                    const selectedFilter = {
                        filter: action.payload.filter,
                        categories: action.payload.categories
                    };
                    state.currentSelected = state.currentSelected.set(action.payload.filterTypeAndSubtype, selectedFilter);
                }
            } else {
                const selectedFilter = {
                    filter: action.payload.filter,
                    categories: action.payload.categories
                };
                state.currentSelected = state.currentSelected.set(action.payload.filterTypeAndSubtype, selectedFilter);
            }
            return { ...state};
        case FILTER_ACTIONS.CLEAR_SELECTED_QUERY:
            current = state.current;
            state.currentSelectedQuery = state.currentSelectedQuery.set(current, { });
            state.currentSelectedFormData = setCurrentSelectedFormData(state.currentSelectedFormData, current, undefined);
            return { ...state};
        case FILTER_ACTIONS.SET_SELECTED_QUERY:
            const filterTypeAndSubtype = action.payload.filterTypeAndSubtype;
            state.currentSelectedQuery = state.currentSelectedQuery.set(filterTypeAndSubtype, action.payload.query);
            state.currentSelectedFormData = setCurrentSelectedFormData(state.currentSelectedFormData, filterTypeAndSubtype, action.payload.formData);
            return { ...state};
        case FILTER_ACTIONS.SET_APPLIED_FILTER:
            current = state.current;
            currentAppliedFilters = state.applied.get(current);
            if (currentAppliedFilters) {
                results = immutable.Map<string, Filter>(currentAppliedFilters);
                results = results.asMutable();
                results.set(action.payload.filter.id, action.payload.filter);
                const matchingFilter = currentAppliedFilters.get(action.payload.filter.id);
                const categories = cloneDeep(action.payload.categories);
                if (!matchingFilter) {
                    currentSelectedFilter = {
                        filter: action.payload.filter,
                        categories: categories
                    };
                    // A new filter was created, add it to the list of filters.
                    state.currentSelected = state.currentSelected.set(current, currentSelectedFilter);
                }
                state.applied = state.applied.set(current, results.asImmutable());
            }
            return { ...state};
        case FILTER_ACTIONS.SET_APPLIED_FILTER_CATEGORIES:
            currentSelectedFilter = state.currentSelected.get(action.payload.filterTypeAndSubtype);
            if (currentSelectedFilter && currentSelectedFilter.id == action.payload.filterId) {
                currentSelectedFilter.categories = cloneDeep(action.payload.filterCategories);
            }
            state.currentSelected = state.currentSelected.set(action.payload.filterTypeAndSubtype, currentSelectedFilter);
            return { ...state};
        case FILTER_ACTIONS.DELETE_APPLIED_FILTER:
            current = state.current;
            currentAppliedFilters = state.applied.get(current);
            results = immutable.Map<string, Filter>(currentAppliedFilters);
            results = results.asMutable();
            results = results.delete(action.payload.filterId);
            state.applied = state.applied.set(current, results.asImmutable());
            return { ...state};
        default:
            return state;
    }
}

const setCurrentSelectedFormData = function (currentFormDataMap: immutable.Map<string, any>,
                                             filterTypeAndSubtype: string,
                                             formData: any,
                                             overrideIfExists: boolean = true): immutable.Map<string, any> {

    // if formData is not relevant to the current filterTypeAndSubtype key,
    //  no need to store an empty structure for it in the map. Therefore, just delete this entry from the map
    if (! formData) {
        return currentFormDataMap.delete(filterTypeAndSubtype);
    }

    // if it is indicated NOT to override current entry if it already exists - return the map as is
    if (! overrideIfExists &&
        currentFormDataMap.has(filterTypeAndSubtype)) {

        return currentFormDataMap;
    }

    // Important: clone original formData so it won't point to the same object, which its data might be changed
    return currentFormDataMap.set(filterTypeAndSubtype, cloneDeep(formData));
};
